import { useEffect, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { GoArrowRight } from "react-icons/go";

import ToolTip from "../../../shared/components/tooltip/tooltip";
import "./multi-tab-component.scss";

export interface ITab {
  name: string;
  key: string;
  content: React.ReactNode;
  isVisible?(): boolean;
}

export interface IMultiTabComponentConfig {
  tabs: ITab[];
  activeTab?: string;
  displayNextButton?: boolean;
  enableHashRouting?: boolean;
}

interface IIndexedTab extends ITab {
  index: number;
  isVisible(): boolean;
}

const MultiTabComponent = ({tabs, activeTab, displayNextButton, enableHashRouting}: IMultiTabComponentConfig) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [indexedTabs, setIndexedTabs] = useState<IIndexedTab[]>([]);
  const isInitialized = useRef(false);
  const userHasInteracted = useRef(false);

  useEffect(() => {
    const newIndexedTabs = tabs.map((tab, i) => ({
      ...tab,
      index: i,
      isVisible: () => tab.isVisible?.() ?? true
    }));

    const visibleTabs = newIndexedTabs.filter(tab => tab.isVisible());
    setIndexedTabs(newIndexedTabs);

    if (visibleTabs.length === 0) return;
    let shouldSetInitialTab = !isInitialized.current;
    if (enableHashRouting) {
      const hash = window.location.hash.replace("#", "");
      const matchedTab = visibleTabs.find(tab => tab.key === hash);
      if (matchedTab && matchedTab.index !== selectedTab) {
        setSelectedTab(matchedTab.index);
        shouldSetInitialTab = false;
      }
    }
    
    else if (activeTab && !userHasInteracted.current) {
      const matchedTab = visibleTabs.find(tab => tab.key === activeTab);
      if (matchedTab && matchedTab.index !== selectedTab) {
        setSelectedTab(matchedTab.index);
        shouldSetInitialTab = false;
      }
    }
    if (shouldSetInitialTab || !visibleTabs.some(tab => tab.index === selectedTab)) {
      const initialIndex = visibleTabs[0].index;
      setSelectedTab(initialIndex);
    }
    isInitialized.current = true;
  }, [tabs, activeTab, enableHashRouting, selectedTab]);

  const handleTabClick = (index: number, key: string) => {
    userHasInteracted.current = true;
    setSelectedTab(index);
    if (enableHashRouting) {
      window.history.replaceState(null, "", `#${key}`);
    }
  };

  const renderTabs = () => {
    return indexedTabs.map(tab => {
      if (!tab.isVisible()){
        return undefined;
      }

      return (
        <div
          role="tab"
          className={`${selectedTab === tab.index ? "multiTabComponent-tabSelected" : ""} multiTabComponent-tab`}
          key={tab.index}
          onClick={() => handleTabClick(tab.index, tab.key)}
        >
          {tab.name}
        </div>
      );
    });
  };

  const nextTab = () => {
    const next = indexedTabs.find(tab => tab.index > selectedTab && tab.isVisible());
    if (next) {
      userHasInteracted.current = true;
      setSelectedTab(next.index);
      if (enableHashRouting) {
        window.history.replaceState(null, "", `#${next.key}`);
      }
    }
  };

  const showNextButton = () => {
    return (
      indexedTabs.some(
        tab => tab.index > selectedTab && tab.isVisible()
      ) && (displayNextButton ?? true)
    );
  };

  const selected = indexedTabs[selectedTab];

  return (
    <div className="multiTabComponent">
      <div className="multiTabComponent-tabs">{renderTabs()}</div>
      <div className="multiTabComponent-child" key={selected?.key}>
        {selected?.isVisible() ? selected.content : null}
        {showNextButton() && (
          <div className="multiTabComponent-nextButton">
            <ToolTip content={"Next"} direction="top" delay={350}>
              <Button
                name="next"
                className="multiTabComponent-next"
                onClick={nextTab}
              >
                <GoArrowRight />
              </Button>
            </ToolTip>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiTabComponent;